import { FocusTrap } from "vendor";

const strings = JSON.parse(document.getElementById("accessibility-strings")?.textContent || "{}");
const modalStack = [];
const inerted = new Map();
const customModals = new WeakSet();
const modalStates = new WeakMap();
const tabSets = new WeakSet();
let tabId = 0;

function announce(message, urgent = false) {
  const region = document.getElementById(urgent ? "error-announcement" : "status-announcement");
  if (!region || !message) return;
  clearTimeout(region._announcementTimer);
  region.textContent = "";
  region._announcementTimer = setTimeout(() => { region.textContent = message; }, 100);
}

function updateBackground() {
  inerted.forEach((value, node) => { node.inert = value; });
  inerted.clear();
  const top = modalStack.at(-1);
  if (!top?.isConnected) return;
  let node = top;
  while (node?.parentElement && node !== document.body) {
    for (const sibling of node.parentElement.children) {
      if (sibling === node || sibling.matches('script, style, link, [role="status"], [role="alert"]')) continue;
      inerted.set(sibling, sibling.inert);
      sibling.inert = true;
    }
    node = node.parentElement;
  }
}

function setModalOpen(modal, open) {
  const index = modalStack.indexOf(modal);
  if (open && index === -1) modalStack.push(modal);
  if (!open && index !== -1) modalStack.splice(index, 1);
  updateBackground();
}

function visible(node) {
  return node.isConnected && !node.hidden && getComputedStyle(node).display !== "none" && getComputedStyle(node).visibility !== "hidden" && node.getClientRects().length > 0;
}

function initializeModal(modal) {
  if (customModals.has(modal)) return;
  customModals.add(modal);
  let open = false;
  let returnFocus = null;
  const close = () => modal.querySelector(modal.dataset.a11yClose)?.click();
  const syncControls = () => {
    if (!modal.id) return;
    document.querySelectorAll(`[aria-controls="${CSS.escape(modal.id)}"]`).forEach(control => {
      control.setAttribute("aria-expanded", String(open));
      control.setAttribute("aria-haspopup", "dialog");
    });
  };
  const trap = FocusTrap.createFocusTrap(modal, {
    initialFocus: modal,
    setReturnFocus: () => returnFocus?.isConnected ? returnFocus : false,
    fallbackFocus: modal,
    preventScroll: true,
    escapeDeactivates: true,
    onDeactivate: close,
    allowOutsideClick: true,
    tabbableOptions: { getShadowRoot: true }
  });
  const sync = () => {
    const next = visible(modal);
    if (next === open) return;
    open = next;
    if (open) returnFocus = document.activeElement;
    setModalOpen(modal, open);
    syncControls();
    modal.removeAttribute("aria-hidden");
    if (open) {
      modal.removeAttribute("inert");
      trap.activate();
    } else {
      trap.deactivate({ onDeactivate: () => {} });
      modal.setAttribute("inert", "");
    }
  };
  modalStates.set(modal, { sync, trap });
  new MutationObserver(sync).observe(modal, { attributes: true, attributeFilter: ["class", "style", "hidden"] });
  sync();
  syncControls();
  if (!open) {
    modal.setAttribute("inert", "");
  }
}

function initializeTabs(group) {
  if (tabSets.has(group)) return;
  const scope = group.closest(".shopify-section") || group.parentElement;
  const tabs = [...group.querySelectorAll(".tab-title-item")];
  const panels = [...scope.querySelectorAll(".tab-content-box > .tab-content, .tab-content-box > .tab-content-item-box")];
  if (tabs.length !== panels.length || tabs.length === 0) return;
  tabSets.add(group);
  const list = tabs[0].parentElement;
  list.setAttribute("role", "tablist");
  const id = ++tabId;
  const sync = () => tabs.forEach((tab, index) => {
    const selected = tab.classList.contains("active");
    tab.setAttribute("aria-selected", String(selected));
    tab.tabIndex = selected ? 0 : -1;
    panels[index].hidden = !selected;
  });
  tabs.forEach((tab, index) => {
    tab.id ||= `legacy-tab-${id}-${index}`;
    panels[index].id ||= `legacy-panel-${id}-${index}`;
    tab.setAttribute("role", "tab");
    tab.setAttribute("aria-controls", panels[index].id);
    panels[index].setAttribute("role", "tabpanel");
    panels[index].setAttribute("aria-labelledby", tab.id);
    panels[index].tabIndex = 0;
    tab.addEventListener("click", () => queueMicrotask(sync));
    new MutationObserver(sync).observe(tab, { attributes: true, attributeFilter: ["class"] });
  });
  sync();
}

function initialize(root) {
  if (!(root instanceof Element || root instanceof Document)) return;
  const modals = [...root.querySelectorAll("[data-a11y-modal]")];
  if (root.matches?.("[data-a11y-modal]")) modals.push(root);
  modals.forEach(initializeModal);
  const groups = [...root.querySelectorAll("[data-a11y-tabs]")];
  if (root.matches?.("[data-a11y-tabs]")) groups.push(root);
  groups.forEach(initializeTabs);
}

document.addEventListener("click", event => {
  const skip = event.target.closest(".skip-to-content");
  if (!skip) return;
  const target = document.getElementById("main");
  if (!target) return;
  event.preventDefault();
  target.focus({ preventScroll: true });
  target.scrollIntoView({ behavior: "instant", block: "start" });
}, true);

document.addEventListener("keydown", event => {
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.repeat) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.matches('[role="tab"]') && !target.closest("x-tabs")) {
    const list = target.closest('[role="tablist"]');
    const tabs = [...(list?.querySelectorAll('[role="tab"]') || [])].filter(tab => tab.closest('[role="tablist"]') === list);
    const index = tabs.indexOf(target);
    if (index !== -1 && ["ArrowLeft", "ArrowRight", "Home", "End", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      let next = index;
      if (event.key === "Home") next = 0;
      if (event.key === "End") next = tabs.length - 1;
      if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
      if (event.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
      tabs[next].focus();
      tabs[next].click();
      return;
    }
  }
  if ((target.matches('[role="button"]') && !target.matches("button, a[href], input, select, textarea, summary")) || target.matches("[data-a11y-activate]")) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (target.getAttribute("aria-disabled") !== "true") {
        if (typeof target.click === "function") target.click();
        else target.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
      }
    }
  }
});

document.addEventListener("toggle", event => {
  const dialog = event.target;
  if (dialog instanceof Element && dialog.matches('[role="dialog"][aria-modal="true"]:not([data-a11y-modal])')) {
    setModalOpen(dialog, dialog.hasAttribute("open"));
  }
}, true);

document.addEventListener("cart:change", event => {
  const count = event.detail?.cart?.item_count;
  if (count !== undefined && event.detail?.baseEvent !== "variant:add") announce(strings.cart_updated?.replace("__count__", count));
});
document.addEventListener("cart:error", event => announce(event.detail?.error, true));
document.addEventListener("line-item:error", event => announce(event.detail?.error || event.detail?.message, true));
document.addEventListener("accessibility:announce", event => announce(event.detail?.message, event.detail?.urgent));

initialize(document);
new MutationObserver(records => {
  let removed = false;
  for (const record of records) {
    record.addedNodes.forEach(initialize);
    removed ||= record.removedNodes.length > 0;
  }
  if (removed) {
    for (const modal of [...modalStack]) {
      if (modal.isConnected) continue;
      modalStates.get(modal)?.trap.deactivate({ returnFocus: false, onDeactivate: () => {} });
      setModalOpen(modal, false);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
